import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { STOCK, BROWN } from '../assets';

gsap.registerPlugin(ScrollTrigger);

export default function Ch5Stitching() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top top',
          end: '+=180%',
          scrub: 1,
          pin: true,
        },
      });
      tl.fromTo('.panel-l', { xPercent: -70, opacity: 0.4 }, { xPercent: 0, opacity: 1, duration: 1, ease: 'none' })
        .fromTo('.panel-r', { xPercent: 70, opacity: 0.4 }, { xPercent: 0, opacity: 1, duration: 1, ease: 'none' }, '<')
        .fromTo('.seam-wrap', { clipPath: 'inset(0 0 100% 0)' }, { clipPath: 'inset(0 0 0% 0)', duration: 1.2, ease: 'none' })
        .fromTo('.stitch-caption', { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.5 }, '-=0.5')
        .fromTo('.seam-count', { textContent: 0 }, { textContent: 1200, snap: { textContent: 10 }, duration: 1.2, ease: 'none' }, '<');
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section id="ch-5" ref={sectionRef} data-testid="chapter-5" className="relative h-screen overflow-hidden bg-[#0d0b09] flex items-center justify-center">
      <div className="absolute inset-0 opacity-15">
        <img src={STOCK.sewingMachine} alt="Industrial sewing" className="w-full h-full object-cover" />
      </div>
      <div className="absolute top-24 left-6 md:left-16 z-20">
        <div className="flex items-center gap-4">
          <span className="font-accent text-[10px] md:text-xs text-[#8C5A35]">CH. 05</span>
          <span className="h-px w-12 md:w-20 bg-[#B89B72]/40" />
          <span className="font-accent text-[10px] md:text-xs text-[#A3A3A3]">Stitching</span>
        </div>
      </div>

      <div className="relative z-10 flex items-stretch w-[86vw] max-w-3xl aspect-[16/10]">
        <div className="panel-l relative w-1/2 overflow-hidden border border-[#2A2A2A] will-change-transform">
          <img src={BROWN.chest} alt="Shoulder panel — left half" className="absolute inset-y-0 left-0 h-full max-w-none object-cover" style={{ width: '200%' }} />
          <div className="absolute inset-0 bg-[#0A0A0A]/20" />
        </div>
        <div className="panel-r relative w-1/2 overflow-hidden border border-[#2A2A2A] will-change-transform">
          <img src={BROWN.chest} alt="Shoulder panel — right half" className="absolute inset-y-0 right-0 h-full max-w-none object-cover" style={{ width: '200%' }} />
          <div className="absolute inset-0 bg-[#0A0A0A]/20" />
        </div>
        <div className="seam-wrap absolute left-1/2 top-0 bottom-0 w-8 -ml-4 z-10 will-change-[clip-path]" style={{ clipPath: 'inset(0 0 100% 0)' }}>
          <svg className="w-full h-full" viewBox="0 0 20 100" preserveAspectRatio="none" fill="none">
            <path d="M10 0 V100" stroke="#B89B72" strokeWidth="1.6" strokeDasharray="3 2" />
          </svg>
        </div>
      </div>

      <div className="stitch-caption absolute bottom-14 md:bottom-20 right-6 md:right-16 max-w-md text-right opacity-0 z-20">
        <div className="font-display text-5xl md:text-7xl font-light text-[#B89B72]">
          <span className="seam-count">0</span>
        </div>
        <span className="font-accent text-[9px] text-[#A3A3A3] block mb-4">stitches per jacket</span>
        <h2 className="font-display font-light text-3xl md:text-4xl leading-tight tracking-tight">
          Panels become <span className="italic text-[#B89B72]">one.</span>
        </h2>
        <p className="font-body font-light text-sm text-[#A3A3A3] mt-3 leading-relaxed">
          Two halves of one shoulder panel, rejoined stitch by stitch —
          bonded nylon thread, eight to the inch, every seam sewn twice.
        </p>
      </div>
    </section>
  );
}
